import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { getTranscriptionAPI, syncTranscriptionAPI } from '../../../context-api/api-services/twilio.service';

/*
  Label convention:
    side === 'customer'  →  our regional branch caller  →  "Agent"
    side === 'agent'     →  kitchen / lead on the call  →  kitchen name or "Customer"
*/

function fmtTime(seconds) {
  if (seconds == null) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getSpeakerInfo(msg, customer_name) {
  if (msg.side === 'customer') {
    return { label: 'Agent', is_agent: true };
  }
  return { label: customer_name || msg.name || 'Customer', is_agent: false };
}

/*
  Group consecutive messages from the same side so the speaker name
  only appears once per "turn", and all their lines render together.
*/
function groupMessages(messages) {
  const groups = [];
  messages.forEach((msg) => {
    const prev = groups[groups.length - 1];
    if (prev && prev.side === msg.side) {
      prev.items.push(msg);
    } else {
      groups.push({ side: msg.side, name: msg.name, items: [msg] });
    }
  });
  return groups;
}

function TranscriptTurn({ group, customer_name, on_seek }) {
  const { label, is_agent } = getSpeakerInfo(group.items[0], customer_name);
  const first_time = fmtTime(group.items[0].start_time_seconds);

  // Merge all sentences from this speaker into one continuous paragraph
  const full_text = group.items.map((m) => m.text.trim()).filter(Boolean).join(' ');

  const can_seek = typeof on_seek === 'function' && group.items[0].start_time_seconds != null;

  return (
    <Box
      onClick={can_seek ? () => on_seek(group.items[0].start_time_seconds) : undefined}
      sx={{
        display: 'flex',
        gap: 1.5,
        minWidth: 0,
        width: '100%',
        cursor: can_seek ? 'pointer' : 'default',
        borderRadius: '8px',
        py: 0.5,
        px: 0.75,
        transition: 'background 0.12s',
        '&:hover': can_seek ? { bgcolor: is_agent ? '#F3F4F6' : '#F8F8F8' } : undefined,
      }}
    >
      {/* Left accent strip */}
      <Box
        sx={{
          width: 3,
          minHeight: '100%',
          borderRadius: '3px',
          flexShrink: 0,
          alignSelf: 'stretch',
          bgcolor: is_agent ? '#111111' : '#D1D5DB',
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Speaker + timestamp */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.35 }}>
          <Typography
            sx={{
              fontFamily: 'Inter Bold, sans-serif',
              fontWeight: 700,
              fontSize: 12.5,
              color: is_agent ? '#111111' : '#374151',
              lineHeight: 1,
            }}
          >
            {label}
          </Typography>
          {first_time && (
            <Typography
              sx={{
                fontFamily: 'Inter Regular, sans-serif',
                fontSize: 11,
                color: '#9CA3AF',
                lineHeight: 1,
              }}
            >
              {first_time}
            </Typography>
          )}
        </Box>

        {/* Full merged paragraph — no breaks until next speaker */}
        <Typography
          sx={{
            fontFamily: 'Inter Regular, sans-serif',
            fontSize: 13.5,
            color: '#1F2937',
            lineHeight: 1.7,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {full_text}
        </Typography>
      </Box>
    </Box>
  );
}

export function RecordingTranscript({
  recording_sid,
  initial_transcription,
  customer_name,
  on_seek,
}) {
  const [text, setText] = useState(initial_transcription?.transcription_text || '');
  const [communication, setCommunication] = useState(
    Array.isArray(initial_transcription?.communication) ? initial_transcription.communication : [],
  );
  const [status, setStatus] = useState(initial_transcription?.transcription_status || 'none');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const applyResponse = (data) => {
    const s = data?.transcription_status || 'none';
    setStatus(s);
    if (s === 'completed') {
      setText(data?.transcription_text || '');
      setCommunication(Array.isArray(data?.communication) ? data.communication : []);
    } else {
      setText('');
      setCommunication([]);
    }
  };

  const fetchTranscript = async () => {
    if (!recording_sid) return;
    setLoading(true);
    setError(null);
    const res = await getTranscriptionAPI(recording_sid);
    setLoading(false);
    if (res?.hasError) { setError(res.message || 'Failed to load transcript.'); return; }
    applyResponse(res.data || res);
  };

  const requestTranscript = async () => {
    if (!recording_sid) return;
    setLoading(true);
    setError(null);
    const res = await syncTranscriptionAPI(recording_sid);
    setLoading(false);
    if (res?.hasError) { setError(res.message || 'Failed to request transcript.'); return; }
    if (!res.data) { setStatus('none'); return; }
    applyResponse(res.data);
  };

  useEffect(() => {
    const s = initial_transcription?.transcription_status;
    const t = initial_transcription?.transcription_text;
    const messages = initial_transcription?.communication;

    if (s === 'completed' && (t || (Array.isArray(messages) && messages.length > 0))) {
      setText(t || '');
      setCommunication(Array.isArray(messages) ? messages : []);
      setStatus('completed');
      return;
    }

    if (s === 'none' || !s) {
      requestTranscript();
      return;
    }

    fetchTranscript();
  }, [recording_sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const has_chat = communication.length > 0;
  const has_fallback_text = Boolean(text);
  const groups = has_chat ? groupMessages(communication) : [];

  return (
    <Box
      sx={{
        mt: 2,
        border: '1.5px solid #E5E7EB',
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #EFEFEF',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: '0.02em' }}
        >
          TRANSCRIPT
        </Typography>

        {status === 'pending' && !loading && (
          <Button
            size='small'
            onClick={fetchTranscript}
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 13 }} />}
            sx={{
              textTransform: 'none', fontSize: 11.5, px: 1.25, py: 0.25, minWidth: 0,
              color: '#374151', fontFamily: 'Inter Regular, sans-serif',
              border: '1px solid #E5E7EB', borderRadius: '7px',
              '&:hover': { bgcolor: '#F3F4F6' },
            }}
          >
            Check again
          </Button>
        )}

        {status === 'completed' && !loading && (
          <Button
            size='small'
            onClick={fetchTranscript}
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 13 }} />}
            sx={{
              textTransform: 'none', fontSize: 11.5, px: 1, minWidth: 0,
              color: '#C0C0C0', fontFamily: 'Inter Regular, sans-serif',
              '&:hover': { color: '#9CA3AF' },
            }}
          >
            Refresh
          </Button>
        )}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={11} sx={{ color: '#9CA3AF' }} />
            <Typography sx={{ fontSize: 11.5, color: '#9CA3AF', fontFamily: 'Inter Regular, sans-serif' }}>
              Loading…
            </Typography>
          </Box>
        )}
      </Box>{/* /header bar */}

      {/* Body */}
      <Box sx={{ px: 2, py: 1.5, minWidth: 0, overflowX: 'hidden' }}>

      {/* Error */}
      {error && (
        <Typography sx={{ fontSize: 12, color: '#DC2626', fontFamily: 'Inter Regular, sans-serif' }}>
          {error}
        </Typography>
      )}

      {/* No transcript yet */}
      {!error && !loading && status === 'none' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1 }}>
          <NotesOutlinedIcon sx={{ fontSize: 15, color: '#D1D5DB' }} />
          <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', fontFamily: 'Inter Regular, sans-serif' }}>
            No transcript available for this recording.
          </Typography>
        </Box>
      )}

      {/* Pending */}
      {!error && !loading && status === 'pending' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1 }}>
          <HourglassEmptyRoundedIcon sx={{ fontSize: 15, color: '#F59E0B' }} />
          <Typography sx={{ fontSize: 12.5, color: '#B45309', fontFamily: 'Inter Regular, sans-serif' }}>
            Twilio is processing the transcript. Check again in 1–3 minutes.
          </Typography>
        </Box>
      )}

      {/* Failed */}
      {!error && !loading && status === 'failed' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: '#DC2626' }} />
          <Typography sx={{ fontSize: 12.5, color: '#DC2626', fontFamily: 'Inter Regular, sans-serif' }}>
            Transcription failed.
          </Typography>
        </Box>
      )}

      {/* Chat transcript — grouped turns */}
      {!error && status === 'completed' && has_chat && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            maxHeight: 420,
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: 0,
            pr: 0.5,
            pb: 0.5,
          }}
        >
          {groups.map((group, gi) => (
            <TranscriptTurn
              key={gi}
              group={group}
              customer_name={customer_name}
              on_seek={on_seek}
            />
          ))}
        </Box>
      )}

      {/* Fallback: flat text when no communication array */}
      {!error && status === 'completed' && !has_chat && has_fallback_text && (
        <Typography
          sx={{
            fontSize: 13.5,
            color: '#111827',
            fontFamily: 'Inter Regular, sans-serif',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {text}
        </Typography>
      )}

      {/* Completed but truly empty */}
      {!error && !loading && status === 'completed' && !has_chat && !has_fallback_text && (
        <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', fontFamily: 'Inter Regular, sans-serif', fontStyle: 'italic' }}>
          Transcript marked complete but no messages are available.
        </Typography>
      )}

      </Box>{/* /body */}
    </Box>
  );
}
